import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Flex, Text, SimpleGrid, Skeleton, Input, Button, Select, Badge,
  useToast, Icon, VStack, HStack, Progress, Collapse
} from '@chakra-ui/react';
import { Card, Title } from "@tremor/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie
} from 'recharts';
import {
  DollarSign, TrendingUp, ShoppingBag, Percent,
  CircleDollarSign, RefreshCw, Calendar, ArrowUpCircle,
  ChevronDown, ChevronRight, Package, Banknote, Download
} from 'lucide-react';
import { DRE_ORIGENS, paramsDaOrigem } from '../utils/dreOrigens';
import ScrollToTop from "../components/ScrollToTop";
import { getDre, getDreSyncStatus, exportarDre } from "../services/api";

const formatMoney = (val) => `R$ ${parseFloat(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const formatPercent = (val) => `${parseFloat(val || 0).toFixed(2)}%`;
const COLORS_PIE = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#6366f1'];

// ==================== COMPONENTES VISUAIS ====================

const MetricCard = ({ label, value, icon, color, isLoading, isMoney = true }) => (
  <Card className="ring-0">
    <Flex align="center" gap={3}>
      <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg={`${color}.50`}>
        <Icon as={icon} boxSize={5} color={`${color}.500`} />
      </Flex>
      <Box flex={1}>
        <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase">{label}</Text>
        {isLoading ? <Skeleton h="28px" w="120px" mt={1} /> : (
          <Text fontSize="xl" fontWeight="bold" color="gray.800">{isMoney ? formatMoney(value) : value}</Text>
        )}
      </Box>
    </Flex>
  </Card>
);

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-slate-200 shadow-lg rounded-lg z-50">
        <p className="text-xs font-bold text-slate-700 mb-2 border-b pb-1">{payload[0].name}</p>
        <p className="text-sm text-slate-600">{formatMoney(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

// --- DRE row components ---

const DreHeader = ({ label, value, bg, color }) => (
  <Flex bg={bg} px={4} py={2.5} justify="space-between" align="center" borderRadius="md">
    <Text fontWeight="bold" fontSize="sm" color={color}>{label}</Text>
    <Text fontWeight="bold" fontSize="sm" color={color}>{formatMoney(value)}</Text>
  </Flex>
);

const DreSubtotal = ({ label, value, color = "gray.700" }) => (
  <Flex px={4} py={2} justify="space-between" align="center" bg="gray.50" borderRadius="md" borderLeft="3px solid" borderLeftColor={color}>
    <Text fontWeight="semibold" fontSize="sm" color={color}>{label}</Text>
    <Text fontWeight="bold" fontSize="sm" color={color}>{formatMoney(value)}</Text>
  </Flex>
);

const DreLine = ({ label, value, isReceita = false }) => (
  <Flex px={4} py={1.5} justify="space-between" align="center" pl="40px"
    _hover={{ bg: 'gray.50' }} borderRadius="sm" transition="background 0.15s"
  >
    <Text fontSize="xs" color="gray.600">
      <Text as="span" color={isReceita ? 'green.400' : 'red.400'} mr={1}>{isReceita ? '(+)' : '(-)'}</Text>{label}
    </Text>
    <Text fontSize="xs" color="gray.600" fontWeight="medium">{formatMoney(value)}</Text>
  </Flex>
);

const DreSpacer = () => <Box h="4px" />;

// Variação de estoque: linha principal (saída - entrada) com breakdown expansível
const DreVariacaoEstoque = ({ variacao, detalhado }) => {
  const [isOpen, setIsOpen] = useState(false);
  const entrada = parseFloat(detalhado?.entrada || 0);
  const saida = parseFloat(detalhado?.saida || 0);
  const entradaQtd = detalhado?.entrada_qtd || 0;
  const saidaQtd = detalhado?.saida_qtd || 0;
  const positivo = (variacao || 0) >= 0;
  const hasDetalhe = entrada > 0 || saida > 0;

  return (
    <Box borderBottom="1px solid" borderColor="gray.100">
      <Flex
        px={4} py={2.5} justify="space-between" align="center"
        cursor={hasDetalhe ? "pointer" : "default"}
        onClick={() => hasDetalhe && setIsOpen(!isOpen)}
        _hover={hasDetalhe ? { bg: positivo ? 'green.50' : 'orange.50' } : {}}
        borderRadius="sm" transition="background 0.15s"
      >
        <Flex align="center" gap={2}>
          {hasDetalhe && <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={3.5} color={positivo ? 'green.400' : 'orange.400'} />}
          <Icon as={Package} boxSize={4} color={positivo ? 'green.500' : 'orange.500'} />
          <Text fontSize="sm" fontWeight="semibold" color={positivo ? 'green.700' : 'orange.700'}>
            ({positivo ? '+' : '−'}) Variação de Estoque
          </Text>
        </Flex>
        <Text fontSize="sm" fontWeight="bold" color={positivo ? 'green.700' : 'orange.700'}>
          {positivo ? '+' : ''}{formatMoney(variacao)}
        </Text>
      </Flex>
      {hasDetalhe && (
        <Collapse in={isOpen} animateOpacity>
          <VStack pl={10} pr={4} pb={2} spacing={0.5} align="stretch">
            <Flex justify="space-between" py={0.5}>
              <Text fontSize="xs" color="green.600">
                <Text as="span" color="green.500" mr={1}>(+)</Text>
                Saída do estoque próprio {saidaQtd > 0 && <Text as="span" color="gray.400">({saidaQtd} venda{saidaQtd === 1 ? '' : 's'})</Text>}
              </Text>
              <Text fontSize="xs" color="green.600" fontWeight="medium">+{formatMoney(saida)}</Text>
            </Flex>
            <Flex justify="space-between" py={0.5}>
              <Text fontSize="xs" color="red.500">
                <Text as="span" color="red.400" mr={1}>(−)</Text>
                Entrada por devolução {entradaQtd > 0 && <Text as="span" color="gray.400">({entradaQtd} venda{entradaQtd === 1 ? '' : 's'})</Text>}
              </Text>
              <Text fontSize="xs" color="red.500" fontWeight="medium">−{formatMoney(entrada)}</Text>
            </Flex>
          </VStack>
        </Collapse>
      )}
    </Box>
  );
};

// ==================== COMPONENTES DRE REESTRUTURADO ====================

// Seção colapsável para custos variáveis (VENDAS, FRETE, PROCESSAMENTO)
// Quebra os lançamentos de uma subcategoria pelo quarto nível (Item). Quem não
// tem item continua saindo direto, sem cabeçalho, como sempre foi.
const quebrarPorItem = (itens = []) => {
  const semItem = [];
  const comItem = new Map();
  itens.forEach((l) => {
    if (l.item) {
      if (!comItem.has(l.item)) comItem.set(l.item, []);
      comItem.get(l.item).push(l);
    } else {
      semItem.push(l);
    }
  });
  return { semItem, comItem: [...comItem.entries()] };
};

const valorLancamento = (l) => l.valor_periodo ?? l.valor ?? 0;
const somaLancamentos = (ls) => ls.reduce((acc, l) => acc + valorLancamento(l), 0);

const formatDataCurta = (iso) => {
  if (!iso) return '';
  const [, mes, dia] = String(iso).substring(0, 10).split('-');
  return dia && mes ? `${dia}/${mes}` : '';
};

// Uma linha de lançamento que vira dropdown quando o backend manda o detalhe
// diário em `lancamentos` — o caso dos custos de IA (#2384), que têm um
// lançamento por dia por origem e afogavam a árvore quando listados direto.
// Nasce fechada, no padrão dos níveis de cima; cada linha de dentro mostra a
// data. Lançamento comum, sem `lancamentos`, continua uma linha simples.
const DreRodapeExpansivel = ({ rodape, recuo, fontSize }) => {
  const [isOpen, setIsOpen] = useState(false);
  const texto = typeof rodape === 'string' ? rodape : rodape.texto;
  const itens = (typeof rodape === 'string' ? [] : rodape.itens) || [];

  return (
    <Box>
      <Flex
        align="center" gap={1.5} pl={recuo + 5} py={0.5} minW={0}
        cursor={itens.length ? 'pointer' : 'default'}
        onClick={() => itens.length && setIsOpen(!isOpen)}
        _hover={itens.length ? { bg: 'gray.50' } : {}}
        borderRadius="sm" transition="background 0.15s"
      >
        {itens.length > 0 && (
          <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={2.5} color="gray.400" flexShrink={0} />
        )}
        <Text fontSize={fontSize} color="gray.400" fontStyle="italic">{texto}</Text>
      </Flex>
      {itens.length > 0 && (
        <Collapse in={isOpen} animateOpacity>
          {itens.map((v) => (
            <Flex key={v.venda_id} justify="space-between" align="flex-start" pl={recuo + 8} py={0.5} gap={2}>
              <Flex align="center" gap={2} minW={0} wrap="wrap">
                <Text fontSize={fontSize} color="gray.500" fontWeight="medium" flexShrink={0}>
                  {formatDataCurta(v.data)}
                </Text>
                <Text fontSize={fontSize} color="gray.500" fontWeight="medium" flexShrink={0}>
                  #{v.venda_id}
                </Text>
                <Text fontSize={fontSize} color="gray.400" flexShrink={0}>{v.pedido}</Text>
                <Text fontSize={fontSize} color="gray.400" noOfLines={1}>{v.cliente}</Text>
              </Flex>
              <Text fontSize={fontSize} color="gray.400" whiteSpace="nowrap">
                cobrado {formatMoney(v.frete_cobrado)}
              </Text>
            </Flex>
          ))}
        </Collapse>
      )}
    </Box>
  );
};

const DreLinhaComDetalhe = ({
  label,
  valor,
  lancamentos = [],
  recuo = 0,
  fontSize = '2xs',
  cor = 'gray.500',
  corValor,
  italico = true,
  prefixoValor = '',
  marcador = null,
  sufixoContagem = 'dias',
  rodape = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const diarios = lancamentos || [];
  const temDetalhe = diarios.length > 1;
  const estilo = italico ? 'italic' : 'normal';

  // O detalhe diário repete o nome da origem ("IA - Varredura CRM (US$ ...)").
  // Como o nome já está na linha de cima, aqui sobra só o que muda por dia.
  const detalheDoDia = (d) => {
    const resto = String(d.descricao || '').replace(label, '').trim();
    return resto || String(d.descricao || '');
  };

  return (
    <Box>
      <Flex
        justify="space-between" align="center" pl={recuo} py={0.5}
        cursor={temDetalhe ? 'pointer' : 'default'}
        onClick={() => temDetalhe && setIsOpen(!isOpen)}
        _hover={temDetalhe ? { bg: 'gray.50' } : {}}
        borderRadius="sm" transition="background 0.15s"
      >
        <Flex align="center" gap={1.5} minW={0}>
          {temDetalhe && (
            <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={2.5} color="gray.400" flexShrink={0} />
          )}
          {marcador}
          <Text fontSize={fontSize} color={cor} fontStyle={estilo}>{label}</Text>
          {temDetalhe && (
            <Text fontSize={fontSize} color="gray.400" flexShrink={0}>({diarios.length} {sufixoContagem})</Text>
          )}
        </Flex>
        <Text fontSize={fontSize} color={corValor || cor} fontStyle={estilo} whiteSpace="nowrap" ml={2}>
          {prefixoValor}{formatMoney(valor)}
        </Text>
      </Flex>
      {temDetalhe && (
        <Collapse in={isOpen} animateOpacity>
          {diarios.map((d, idx) => (
            <Flex key={idx} justify="space-between" align="center" pl={recuo + 5} py={0.5}>
              <Flex align="center" gap={2} minW={0}>
                {d.data && (
                  <Text fontSize={fontSize} color="gray.500" fontWeight="medium" flexShrink={0}>
                    {formatDataCurta(d.data)}
                  </Text>
                )}
                <Text fontSize={fontSize} color="gray.400">{detalheDoDia(d)}</Text>
              </Flex>
              <Text fontSize={fontSize} color={d.valor < 0 ? 'green.600' : 'gray.400'} whiteSpace="nowrap" ml={2}>
                {formatMoney(d.valor)}
              </Text>
            </Flex>
          ))}
          {rodape && (
            <DreRodapeExpansivel rodape={rodape} recuo={recuo} fontSize={fontSize} />
          )}
        </Collapse>
      )}
    </Box>
  );
};

// Monta as linhas de uma categoria para o DreCollapsibleCost, respeitando os
// níveis que existem: subcategoria (quando há mais de uma) e item (quando há).
const linhasDaCategoria = (catData) => {
  const linhas = [];
  const subs = Object.entries(catData?.subcategorias || {});
  const multiSub = subs.length > 1;
  subs.forEach(([subNome, subData]) => {
    if (multiSub) linhas.push({ label: subNome, value: subData.total || 0, isGroup: true });
    const { semItem, comItem } = quebrarPorItem(subData.itens);
    // `lancamentos` viaja junto para o custo de IA (#2384) poder abrir o
    // detalhe diário também aqui, e não só na árvore.
    semItem.forEach(l => linhas.push({
      label: l.descricao, value: valorLancamento(l), isChild: multiSub, lancamentos: l.lancamentos,
    }));
    comItem.forEach(([nomeItem, ls]) => {
      linhas.push({ label: nomeItem, value: somaLancamentos(ls), isGroup: true });
      ls.forEach(l => linhas.push({
        label: l.descricao, value: valorLancamento(l), isChild: true, lancamentos: l.lancamentos,
      }));
    });
  });
  return linhas;
};

// items suportam: { label, value } normal, { label, value, isGroup: true } para cabeçalho de subcategoria,
// { label, value, isChild: true } para item indentado sob subcategoria
const DreCollapsibleCost = ({ label, total, items = [], prefix = '(-)' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasItems = items.length > 0;

  if ((total || 0) === 0 && !hasItems) return null;

  return (
    <Box borderBottom="1px solid" borderColor="gray.50">
      <Flex
        px={4} py={2} justify="space-between" align="center"
        cursor={hasItems ? "pointer" : "default"}
        onClick={() => hasItems && setIsOpen(!isOpen)}
        _hover={hasItems ? { bg: 'gray.50' } : {}}
        borderRadius="sm" transition="background 0.15s"
      >
        <Flex align="center" gap={2}>
          {hasItems && <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={3.5} color="gray.400" />}
          <Text fontSize="sm" fontWeight="semibold" color="gray.700">{prefix} {label}</Text>
        </Flex>
        <Text fontSize="sm" fontWeight="semibold" color="red.500">{formatMoney(total)}</Text>
      </Flex>
      {hasItems && (
        <Collapse in={isOpen} animateOpacity>
          <VStack pl={10} pr={4} pb={2} spacing={0} align="stretch">
            {items.map((item, idx) =>
              item.isGroup ? (
                <Flex key={idx} justify="space-between" mt={1.5} mb={0.5}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600">{item.label}</Text>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600">{formatMoney(item.value)}</Text>
                </Flex>
              ) : (
                <DreLinhaComDetalhe
                  key={idx}
                  label={item.label}
                  valor={item.value}
                  lancamentos={item.lancamentos}
                  recuo={item.isChild ? 3 : 0}
                  fontSize="xs"
                  cor="gray.400"
                  italico={false}
                  sufixoContagem={item.sufixoContagem}
                  rodape={item.rodape}
                />
              )
            )}
          </VStack>
        </Collapse>
      )}
    </Box>
  );
};

// Item de categoria expandível dentro das Despesas Operacionais (preserva subcategorias)
const DreDespesaCatItem = ({ nome, catData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const subcatEntries = Object.entries(catData?.subcategorias || {});
  const hasMultipleSubcats = subcatEntries.length > 1;
  const totalItems = subcatEntries.reduce((acc, [, sub]) => acc + (sub.itens?.length || 0), 0);
  const isExpandable = totalItems > 0;

  return (
    <Box borderTop="1px solid" borderColor="purple.100">
      <Flex
        px={4} py={2.5} justify="space-between" align="center"
        cursor={isExpandable ? "pointer" : "default"}
        onClick={() => isExpandable && setIsOpen(!isOpen)}
        _hover={isExpandable ? { bg: 'purple.100' } : {}}
        transition="background 0.15s"
      >
        <Flex align="center" gap={2}>
          {isExpandable && <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={3} color="purple.400" />}
          <Text fontSize="xs" fontWeight="semibold" color="gray.700">{nome}</Text>
        </Flex>
        <Text fontSize="xs" fontWeight="semibold" color="gray.700">{formatMoney(catData.total)}</Text>
      </Flex>
      {isExpandable && (
        <Collapse in={isOpen} animateOpacity>
          <VStack pl={8} pr={4} pb={2} spacing={0} align="stretch">
            {subcatEntries.map(([subNome, subData]) => {
              const { semItem, comItem } = quebrarPorItem(subData.itens);
              const lancamento = (l, chave, recuo) => (
                <DreLinhaComDetalhe
                  key={chave}
                  label={l.descricao}
                  valor={valorLancamento(l)}
                  lancamentos={l.lancamentos}
                  recuo={recuo}
                />
              );
              const blocoItens = comItem.map(([nomeItem, ls]) => (
                <Box key={`item-${nomeItem}`}>
                  <Flex justify="space-between" align="center" pl={hasMultipleSubcats ? 3 : 0} mt={1} mb={0.5}>
                    <Text fontSize="2xs" fontWeight="semibold" color="orange.600">{nomeItem}</Text>
                    <Text fontSize="2xs" fontWeight="semibold" color="orange.600">{formatMoney(somaLancamentos(ls))}</Text>
                  </Flex>
                  {ls.map((l, idx) => lancamento(l, `${nomeItem}-${idx}`, hasMultipleSubcats ? 6 : 3))}
                </Box>
              ));

              if (hasMultipleSubcats) {
                return (
                  <Box key={subNome}>
                    <Flex justify="space-between" align="center" mt={1.5} mb={0.5}>
                      <Text fontSize="2xs" fontWeight="semibold" color="gray.600">{subNome}</Text>
                      <Text fontSize="2xs" fontWeight="semibold" color="gray.600">{formatMoney(subData.total || 0)}</Text>
                    </Flex>
                    {semItem.map((l, idx) => lancamento(l, `${subNome}-${idx}`, 3))}
                    {blocoItens}
                  </Box>
                );
              }
              return (
                <Box key={subNome}>
                  {semItem.map((l, idx) => lancamento(l, `${subNome}-${idx}`, 0))}
                  {blocoItens}
                </Box>
              );
            })}
          </VStack>
        </Collapse>
      )}
    </Box>
  );
};

// Seção colapsável para Resultado Financeiro
const DreFinanceiroCollapsible = ({ financeiroGrupo }) => {
  const [isOpen, setIsOpen] = useState(false);
  const catEntries = Object.entries(financeiroGrupo?.categorias || {});

  const allItems = [];
  catEntries.forEach(([catNome, catData]) => {
    const subcatEntries = Object.entries(catData?.subcategorias || {});
    if (subcatEntries.length === 0) {
      allItems.push({ label: catNome, value: catData.total || 0, isReceita: catData.tipo === 'receita' });
    } else {
      subcatEntries.forEach(([, subData]) => {
        (subData.itens || []).forEach(lancamento => {
          allItems.push({
            label: lancamento.item ? `${lancamento.item} › ${lancamento.descricao}` : lancamento.descricao,
            value: valorLancamento(lancamento),
            isReceita: subData.tipo === 'receita'
          });
        });
      });
    }
  });

  let netValue = 0;
  catEntries.forEach(([, catData]) => {
    if (catData.tipo === 'receita') netValue += (catData.total || 0);
    else netValue -= (catData.total || 0);
  });

  const hasItems = allItems.length > 0;
  if (!hasItems && netValue === 0) return null;

  return (
    <Box borderBottom="1px solid" borderColor="gray.100">
      <Flex
        px={4} py={2.5} justify="space-between" align="center"
        cursor={hasItems ? "pointer" : "default"}
        onClick={() => hasItems && setIsOpen(!isOpen)}
        _hover={{ bg: 'green.50' }}
        borderRadius="sm" transition="background 0.15s"
      >
        <Flex align="center" gap={2}>
          {hasItems && <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={3.5} color="green.400" />}
          <Text fontSize="sm" fontWeight="semibold" color="green.600">(+) RESULTADO FINANCEIRO</Text>
        </Flex>
        <Text fontSize="sm" fontWeight="semibold" color="green.600">
          {netValue >= 0 ? '+' : '-'} {formatMoney(Math.abs(netValue))}
        </Text>
      </Flex>
      {hasItems && (
        <Collapse in={isOpen} animateOpacity>
          <VStack pl={10} pr={4} pb={2} spacing={0.5} align="stretch">
            {allItems.map((item, idx) => (
              <Flex key={idx} justify="space-between">
                <Text fontSize="xs" color={item.isReceita ? 'green.500' : 'red.400'} fontStyle="italic">{item.label}</Text>
                <Text fontSize="xs" color={item.isReceita ? 'green.500' : 'red.400'} fontStyle="italic">{formatMoney(item.value)}</Text>
              </Flex>
            ))}
          </VStack>
        </Collapse>
      )}
    </Box>
  );
};

// ==================== COMPONENTES DA ÁRVORE (custos_fixos_arvore / gastos_variaveis_arvore) ====================

// Linha destacada com árvore expansível (para Processamento, Frete fornecedor, etc.)
const DreLineExpandable = ({ label, categoriaData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const total = categoriaData?.total || 0;
  const subcatEntries = Object.entries(categoriaData?.subcategorias || {});

  if (total === 0) return null;

  return (
    <>
      <Flex
        px={4} py={1.5} justify="space-between" align="center" pl="40px"
        cursor={subcatEntries.length > 0 ? "pointer" : "default"}
        onClick={() => subcatEntries.length > 0 && setIsOpen(!isOpen)}
        _hover={{ bg: 'gray.50' }} borderRadius="sm" transition="background 0.15s"
      >
        <Flex align="center" gap={1}>
          {subcatEntries.length > 0 && (
            <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={3} color="gray.400" />
          )}
          <Text fontSize="xs" color="gray.600">
            <Text as="span" color="red.400" mr={1}>(-)</Text>{label}
          </Text>
        </Flex>
        <Text fontSize="xs" color="gray.600" fontWeight="medium">{formatMoney(total)}</Text>
      </Flex>
      {subcatEntries.length > 0 && (
        <Collapse in={isOpen} animateOpacity>
          {subcatEntries.map(([subNome, subData]) => {
            const itens = subData.itens || [];
            const linha = (item, chave) => (
              <DreLinhaComDetalhe
                key={chave}
                label={item.descricao}
                valor={item.valor_periodo ?? item.valor ?? 0}
                lancamentos={item.lancamentos}
                recuo={20}
                cor="gray.400"
                italico={false}
                marcador={<Box w="4px" h="4px" borderRadius="full" bg="gray.300" flexShrink={0} />}
              />
            );

            if (itens.length === 1) {
              return linha(itens[0], subNome);
            }
            return itens.map(item => linha(item, item.id));
          })}
        </Collapse>
      )}
    </>
  );
};

// Nível 4: Item individual (folha)
const DreTreeItem = ({ descricao, valor, isReceita, lancamentos }) => (
  <DreLinhaComDetalhe
    label={descricao}
    valor={valor}
    lancamentos={lancamentos}
    recuo={30}
    cor={isReceita ? 'green.500' : 'gray.400'}
    italico={false}
    prefixoValor={isReceita ? '+' : ''}
    marcador={<Box w="4px" h="4px" borderRadius="full" bg={isReceita ? 'green.300' : 'gray.300'} flexShrink={0} />}
  />
);

// Nível 3: Subcategoria expandível → itens
const DreTreeSubcategoria = ({ nome, total, itens, isReceita }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (itens.length === 1) {
    // Subcategoria com 1 item: mostra item diretamente
    return <DreTreeItem descricao={itens[0].descricao} valor={itens[0].valor_periodo ?? itens[0].valor ?? 0} isReceita={isReceita} lancamentos={itens[0].lancamentos} />;
  }

  return (
    <>
      <Flex
        px={4} py={1} justify="space-between" align="center" pl="96px"
        cursor="pointer" onClick={() => setIsOpen(!isOpen)}
        _hover={{ bg: 'gray.50' }} borderRadius="sm" transition="background 0.15s"
      >
        <Flex align="center" gap={1}>
          <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={2.5} color="gray.400" />
          <Text fontSize="xs" color={isReceita ? 'green.500' : 'gray.500'}>{nome}</Text>
        </Flex>
        <Text fontSize="xs" color={isReceita ? 'green.500' : 'gray.500'} whiteSpace="nowrap" ml={2}>
          {isReceita ? '+' : ''}{formatMoney(total)}
        </Text>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        {itens.map(item => (
          <DreTreeItem key={item.id} descricao={item.descricao} valor={item.valor_periodo ?? item.valor ?? 0} isReceita={isReceita} lancamentos={item.lancamentos} />
        ))}
      </Collapse>
    </>
  );
};

// Nível 2: Categoria expandível → subcategorias (cada subcategoria tem seu próprio tipo)
const DreTreeCategoria = ({ nome, tipo, total, subcategorias }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isReceita = tipo === 'receita';
  const subcatEntries = Object.entries(subcategorias || {});

  return (
    <>
      <Flex
        px={4} py={1.5} justify="space-between" align="center" pl="64px"
        cursor="pointer" onClick={() => setIsOpen(!isOpen)}
        _hover={{ bg: 'gray.50' }} borderRadius="sm" transition="background 0.15s"
      >
        <Flex align="center" gap={1}>
          <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={3} color="gray.400" />
          <Text fontSize="xs" fontWeight="medium" color={isReceita ? 'green.600' : 'gray.600'}>{nome}</Text>
        </Flex>
        <Text fontSize="xs" fontWeight="medium" color={isReceita ? 'green.600' : 'gray.600'} whiteSpace="nowrap" ml={2}>
          {isReceita ? '+' : ''}{formatMoney(total)}
        </Text>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        {subcatEntries.map(([subNome, subData]) => (
          <DreTreeSubcategoria
            key={subNome}
            nome={subNome}
            total={subData.total}
            itens={subData.itens || []}
            isReceita={subData.tipo === 'receita'}
          />
        ))}
      </Collapse>
    </>
  );
};

// Nível 1: Grupo expandível → categorias (ex: "Despesas", "Financeiro")
// tipo pode ser "despesa", "receita" ou "misto"
const DreTreeGrupo = ({ nome, tipo, total, categorias }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isReceita = tipo === 'receita';
  const isMisto = tipo === 'misto';
  const catEntries = Object.entries(categorias || {});

  // Cor do grupo: verde para receita, cinza/vermelho para despesa, amarelo para misto
  const grupoColor = isReceita ? 'green' : isMisto ? 'yellow' : 'gray';

  return (
    <>
      <Flex
        px={4} py={2} justify="space-between" align="center" pl="40px"
        cursor="pointer" onClick={() => setIsOpen(!isOpen)}
        _hover={{ bg: 'gray.50' }} borderRadius="sm" transition="background 0.15s"
      >
        <Flex align="center" gap={1.5}>
          <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={3.5} color={`${grupoColor}.500`} />
          <Text fontSize="xs" fontWeight="semibold" color={isReceita ? 'green.700' : 'gray.700'}>
            {isReceita ? '(+)' : isMisto ? '(±)' : '(-)'} {nome}
          </Text>
        </Flex>
        <Text fontSize="xs" fontWeight="bold" color={isReceita ? 'green.700' : 'gray.700'}>
          {isReceita ? '+' : ''}{formatMoney(total)}
        </Text>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        {catEntries.map(([catNome, catData]) => (
          <DreTreeCategoria
            key={catNome}
            nome={catNome}
            tipo={catData.tipo}
            total={catData.total}
            subcategorias={catData.subcategorias || {}}
          />
        ))}
      </Collapse>
    </>
  );
};

// Mescla duas árvores (custos_fixos_arvore + gastos_variaveis_arvore) em uma só
const mergeArvores = (a1, a2) => {
  const result = {};
  const allGrupos = new Set([...Object.keys(a1 || {}), ...Object.keys(a2 || {})]);

  allGrupos.forEach(grupo => {
    const g1 = (a1 || {})[grupo];
    const g2 = (a2 || {})[grupo];

    if (!g1) { result[grupo] = g2; return; }
    if (!g2) { result[grupo] = g1; return; }

    // Mesclar categorias dos dois
    const mergedCats = {};
    const allCats = new Set([...Object.keys(g1.categorias || {}), ...Object.keys(g2.categorias || {})]);

    allCats.forEach(cat => {
      const c1 = (g1.categorias || {})[cat];
      const c2 = (g2.categorias || {})[cat];

      if (!c1) { mergedCats[cat] = c2; return; }
      if (!c2) { mergedCats[cat] = c1; return; }

      // Mesclar subcategorias
      const mergedSubs = {};
      const allSubs = new Set([...Object.keys(c1.subcategorias || {}), ...Object.keys(c2.subcategorias || {})]);

      allSubs.forEach(sub => {
        const s1 = (c1.subcategorias || {})[sub];
        const s2 = (c2.subcategorias || {})[sub];

        if (!s1) { mergedSubs[sub] = s2; return; }
        if (!s2) { mergedSubs[sub] = s1; return; }

        mergedSubs[sub] = {
          tipo: s1.tipo || s2.tipo,
          total: (s1.total || 0) + (s2.total || 0),
          itens: [...(s1.itens || []), ...(s2.itens || [])],
        };
      });

      mergedCats[cat] = {
        tipo: c1.tipo || c2.tipo,
        total: (c1.total || 0) + (c2.total || 0),
        subcategorias: mergedSubs,
      };
    });

    // Determinar tipo do grupo mesclado
    const tipos = new Set([g1.tipo, g2.tipo].filter(Boolean));
    const mergedTipo = tipos.size > 1 ? 'misto' : tipos.values().next().value || 'despesa';

    result[grupo] = {
      tipo: mergedTipo,
      total: (g1.total || 0) + (g2.total || 0),
      categorias: mergedCats,
    };
  });

  return result;
};

// Ordem preferencial dos grupos na árvore DRE
const GRUPO_ORDER = ['Custo', 'Despesas', 'Financeiro'];
const sortGrupos = (entries) => entries.sort(([a], [b]) => {
  const ia = GRUPO_ORDER.indexOf(a);
  const ib = GRUPO_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
});

// Seção principal: Custos (árvore mesclada)
const DreTreeSection = ({ label, totalLiquido, arvore, color }) => {
  const [isOpen, setIsOpen] = useState(false);
  const grupoEntries = sortGrupos(Object.entries(arvore || {}));

  if (grupoEntries.length === 0) return null;

  return (
    <>
      <Flex
        px={4} py={2} justify="space-between" align="center"
        cursor="pointer" onClick={() => setIsOpen(!isOpen)}
        _hover={{ bg: 'gray.50' }} borderRadius="sm" transition="background 0.15s"
        bg={isOpen ? 'gray.50' : 'transparent'}
      >
        <Flex align="center" gap={1.5}>
          <Icon as={isOpen ? ChevronDown : ChevronRight} boxSize={3.5} color={color} />
          <Text fontSize="xs" fontWeight="bold" color={color}>(-) {label}</Text>
        </Flex>
        <Text fontSize="xs" fontWeight="bold" color={color}>{formatMoney(totalLiquido)}</Text>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        {grupoEntries.map(([grupoNome, grupoData]) => (
          <DreTreeGrupo
            key={grupoNome}
            nome={grupoNome}
            tipo={grupoData.tipo}
            total={grupoData.total}
            categorias={grupoData.categorias || {}}
          />
        ))}
      </Collapse>
    </>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================

const DREConsolidado = () => {
  const toast = useToast();
  const dataFetchedRef = useRef(false);

  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const [dataInicio, setDataInicio] = useState(primeiroDia.toISOString().slice(0, 10));
  const [dataFim, setDataFim] = useState(hoje.toISOString().slice(0, 10));
  const [origemFiltro, setOrigemFiltro] = useState('');
  const [exportando, setExportando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dreData, setDreData] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  const fetchDre = async () => {
    setLoading(true);
    const params = { data_inicio: dataInicio, data_fim: dataFim, ...paramsDaOrigem(origemFiltro) };

    const [dreRes, syncRes] = await Promise.all([
      getDre(params),
      getDreSyncStatus()
    ]);

    if (dreRes) setDreData(dreRes);
    else toast({ title: 'Erro ao carregar DRE', status: 'error', duration: 3000 });
    if (syncRes) setSyncStatus(syncRes);
    setLoading(false);
  };

  useEffect(() => {
    if (!dataFetchedRef.current) {
      fetchDre();
      dataFetchedRef.current = true;
    }
  }, []);

  const baixarExportacao = async () => {
    setExportando(true);
    const params = { data_inicio: dataInicio, data_fim: dataFim, ...paramsDaOrigem(origemFiltro) };

    const blob = await exportarDre(params);
    setExportando(false);

    if (!blob) {
      toast({ title: 'Erro ao exportar o DRE', status: 'error', duration: 3000 });
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dre-${dataInicio}-a-${dataFim}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const d = dreData;

  // Mesclar árvores de custos fixos e variáveis
  const arvoreMesclada = d ? mergeArvores(d.custos_fixos_arvore, d.gastos_variaveis_arvore) : {};

  // Extrair grupos da árvore
  const custoGrupo = arvoreMesclada?.Custo || {};
  const despesasGrupo = arvoreMesclada?.Despesas || {};
  const financeiroGrupo = arvoreMesclada?.Financeiro || {};

  // Extrair categorias específicas de Custo
  const processamentoData = custoGrupo?.categorias?.Processamento || null;
  const freteFornecedorData = custoGrupo?.categorias?.Frete || null;
  const vendasTreeData = custoGrupo?.categorias?.Vendas || null;
  const processamentoTotal = processamentoData?.total || 0;
  const freteFornecedorTotal = freteFornecedorData?.total || 0;
  const vendasTreeTotal = vendasTreeData?.total || 0;

  // Outras categorias de Custo (não Processamento/Frete/Vendas)
  const outrasCustoCats = Object.entries(custoGrupo?.categorias || {}).filter(
    ([nome]) => nome !== 'Processamento' && nome !== 'Frete' && nome !== 'Vendas'
  );
  const outrasCustoTotal = outrasCustoCats.reduce((acc, [, cat]) => acc + (cat.total || 0), 0);

  // Itens para VENDAS colapsável (unifica taxas_ml da API + categoria Vendas da árvore)
  const vendasItems = [];
  // Taxas abertas por plataforma (#2610). API antiga sem a abertura cai na linha unica.
  if (d?.taxas_mercado_livre !== undefined) {
    if ((d.taxas_mercado_livre || 0) > 0) vendasItems.push({ label: 'Taxas Mercado Livre', value: d.taxas_mercado_livre });
    if ((d.taxas_mercado_pago || 0) > 0) vendasItems.push({ label: 'Taxas Mercado Pago (site)', value: d.taxas_mercado_pago });
  } else if ((d?.taxas_ml || 0) > 0) {
    vendasItems.push({ label: 'Taxas Marketplace', value: d.taxas_ml });
  }
  if (vendasTreeData) {
    vendasItems.push(...linhasDaCategoria(vendasTreeData));
  }
  // Totais dos blocos e as linhas derivadas vêm da API (#2239): a tela e o
  // arquivo exportado leem a mesma conta.
  const resumo = d?.resumo_gerencial || {};
  const vendasTotalGeral = resumo.custos_vendas ?? ((d?.taxas_ml || 0) + vendasTreeTotal);

  // Itens para FRETE colapsável
  const freteDetalhe = d?.frete_detalhe || null;
  const freteItems = [];
  if (freteFornecedorTotal > 0) freteItems.push({ label: 'Fornecedor → Taga', value: freteFornecedorTotal });
  if (freteDetalhe) {
    const site = freteDetalhe.site || {};
    if (freteDetalhe.mercado_livre) {
      freteItems.push({ label: 'Taga → Cliente (Mercado Livre)', value: freteDetalhe.mercado_livre });
    }
    if (site.total || site.custo_envio || site.cobrado_cliente) {
      freteItems.push({
        label: 'Taga → Cliente (Site)',
        value: site.total || 0,
        sufixoContagem: 'linhas',
        rodape: site.vendas_sem_custo
          ? {
            texto: `${site.vendas_sem_custo} venda${site.vendas_sem_custo > 1 ? 's' : ''} sem custo de frete conhecido ${site.vendas_sem_custo > 1 ? 'ficaram' : 'ficou'} fora`,
            itens: site.vendas_sem_custo_lista || [],
          }
          : null,
        lancamentos: [
          { descricao: 'Frete cobrado do cliente', valor: -(site.cobrado_cliente || 0) },
          { descricao: 'Custo de envio', valor: site.custo_envio || 0 },
          { descricao: 'Custo de logística reversa', valor: site.custo_reversa || 0 },
        ],
      });
    }
    if (freteDetalhe.outros) {
      freteItems.push({ label: 'Taga → Cliente (outros canais)', value: freteDetalhe.outros });
    }
  } else if ((d?.frete || 0) > 0) {
    freteItems.push({ label: 'Taga → Cliente', value: d.frete });
  }
  const freteTotalGeral = resumo.custos_frete ?? ((d?.frete || 0) + freteFornecedorTotal);

  // Itens para PROCESSAMENTO colapsável (com subcategorias)
  const processamentoItems = processamentoData ? linhasDaCategoria(processamentoData) : [];

  // Categorias do grupo Despesas: só para listar na tela (o total vem da API)
  const despesasCatEntries = Object.entries(despesasGrupo?.categorias || {});

  // Subtotais intermediários (calculados no backend desde o #2239)
  const margemContribuicao = resumo.margem_contribuicao ?? 0;
  const despesasTotalLiq = resumo.despesas_operacionais ?? 0;
  const lucroOperacional = resumo.lucro_operacional ?? 0;
  const financeiroTotalLiq = resumo.resultado_financeiro ?? 0;

  // Gráficos
  const waterfallData = d ? [
    { name: 'Receita Bruta', valor: d.receita_bruta, fill: '#3b82f6' },
    ...(((d.cancelamentos_parciais || 0) + (d.devolucoes_parciais || 0)) > 0 ? [{ name: 'Cancel./Devol.', valor: -((d.cancelamentos_parciais || 0) + (d.devolucoes_parciais || 0)), fill: '#ef4444' }] : []),
    { name: 'Descontos', valor: -(d.descontos || 0), fill: '#ef4444' },
    { name: 'CMV', valor: -(d.cmv || 0), fill: '#ef4444' },
    { name: 'Taxas ML/MP', valor: -(d.taxas_ml || 0), fill: '#f97316' },
    { name: 'Frete', valor: -(d.frete || 0), fill: '#f59e0b' },
    { name: 'Impostos', valor: -(d.impostos || 0), fill: '#8b5cf6' },
    { name: 'Custos/Desp.', valor: -((d.custos_fixos || 0) + (d.gastos_variaveis || 0)), fill: '#6366f1' },
    { name: 'Lucro Líq.', valor: d.lucro_liquido, fill: d.lucro_liquido >= 0 ? '#10b981' : '#ef4444' },
    { name: 'Var. Estoque', valor: d.variacao_estoque || 0, fill: (d.variacao_estoque || 0) >= 0 ? '#14b8a6' : '#f97316' },
    { name: 'Geração Caixa', valor: d.geracao_caixa || 0, fill: (d.geracao_caixa || 0) >= 0 ? '#059669' : '#ef4444' },
  ] : [];

  const custosPieData = d ? [
    { name: 'CMV', value: d.cmv },
    { name: 'Taxas ML/MP', value: d.taxas_ml },
    { name: 'Frete', value: d.frete },
    { name: 'Impostos', value: d.impostos },
    { name: 'Custos e Despesas', value: Math.max(0, (d.custos_fixos || 0) + (d.gastos_variaveis || 0)) },
  ].filter(i => i.value > 0) : [];

  const syncPercent = syncStatus && syncStatus.total_vendas > 0
    ? ((syncStatus.com_dados_dre / syncStatus.total_vendas) * 100).toFixed(1) : 0;

  // Itens para outras categorias de Custo (se houver)
  const outrasCustoItems = outrasCustoCats.map(([catNome, catData]) => ({
    nome: catNome,
    total: catData.total,
    items: linhasDaCategoria(catData),
  }));

  return (
    <Box className="min-h-screen bg-gray-50" pt="60px">
      <ScrollToTop />
      <Box maxW="1400px" mx="auto" px={{ base: 3, md: 6 }} py={6}>
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">DRE - Demonstrativo de Resultado</Text>
            <Text fontSize="sm" color="gray.500">Visão consolidada do resultado financeiro</Text>
          </Box>
          {syncStatus && (
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="xs">
              {syncStatus.com_dados_dre}/{syncStatus.total_vendas} vendas sincronizadas ({syncPercent}%)
            </Badge>
          )}
        </Flex>

        {/* Filtros */}
        <Card className="ring-0 mb-6">
          <Flex gap={3} flexWrap="wrap" align="end">
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Data Início</Text>
              <Input type="date" size="sm" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} bg="white" borderRadius="md" />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Data Fim</Text>
              <Input type="date" size="sm" value={dataFim} onChange={(e) => setDataFim(e.target.value)} bg="white" borderRadius="md" />
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Origem</Text>
              <Select size="sm" value={origemFiltro} onChange={(e) => setOrigemFiltro(e.target.value)} bg="white" borderRadius="md" placeholder="Todas">
                {DRE_ORIGENS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </Box>
            <Button size="sm" colorScheme="blue" leftIcon={<RefreshCw size={14} />} onClick={fetchDre} isLoading={loading} px={6}>
              Filtrar
            </Button>
            <Button
              size="sm" variant="outline" colorScheme="green"
              leftIcon={<Download size={14} />}
              onClick={baixarExportacao}
              isLoading={exportando}
              loadingText="Gerando"
              px={6}
            >
              Exportar .xlsx
            </Button>
          </Flex>
          {d?.periodo && (
            <Flex mt={3} gap={2} align="center">
              <Icon as={Calendar} boxSize={4} color="gray.400" />
              <Text fontSize="xs" color="gray.400">
                Período: {new Date(d.periodo.inicio).toLocaleDateString('pt-BR')} até {new Date(d.periodo.fim).toLocaleDateString('pt-BR')} ({d.periodo.dias} dias)
              </Text>
            </Flex>
          )}
          {origemFiltro && (
            <Text fontSize="xs" color="blue.500" mt={2}>
              Visão por canal: despesas overhead e não classificadas ficam fora; despesas atribuíveis entram rateadas pela receita dos canais vinculados.
            </Text>
          )}
        </Card>

        {/* Cards */}
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 7 }} spacing={4} mb={6}>
          <MetricCard label="Receita Bruta" value={d?.receita_bruta} icon={ArrowUpCircle} color="blue" isLoading={loading} />
          <MetricCard label="Receita Líquida" value={d?.receita_liquida} icon={DollarSign} color="cyan" isLoading={loading} />
          <MetricCard label="Lucro Bruto" value={d?.lucro_bruto} icon={TrendingUp} color="teal" isLoading={loading} />
          <MetricCard label="Lucro Líquido" value={d?.lucro_liquido} icon={CircleDollarSign} color={d?.lucro_liquido >= 0 ? "green" : "red"} isLoading={loading} />
          <MetricCard label="Geração de Caixa" value={d?.geracao_caixa} icon={Banknote} color={d?.geracao_caixa >= 0 ? "green" : "red"} isLoading={loading} />
          <MetricCard label="Margem Líquida" value={formatPercent(d?.margem_liquida)} icon={Percent} color="purple" isLoading={loading} isMoney={false} />
          <MetricCard label="Total Vendas" value={d?.meta?.total_vendas || 0} icon={ShoppingBag} color="orange" isLoading={loading} isMoney={false} />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
          {/* ==================== TABELA DRE ==================== */}
          <Card className="ring-0">
            <Title className="mb-4">Demonstrativo de Resultado</Title>
            {loading ? (
              <VStack spacing={3}>{[...Array(20)].map((_, i) => <Skeleton key={i} h="20px" w="100%" />)}</VStack>
            ) : d ? (
              <VStack spacing={0.5} align="stretch">

                {/* ── RECEITA BRUTA ── */}
                <DreHeader label="RECEITA BRUTA" value={d.receita_bruta} bg="blue.50" color="blue.700" />
                {(d.pendentes || 0) > 0 && <DreLine label="Pendentes" value={d.pendentes} />}
                {(d.cancelamentos_parciais || 0) > 0 && <DreLine label="Cancelamentos parciais" value={d.cancelamentos_parciais} />}
                {(d.devolucoes_parciais || 0) > 0 && <DreLine label="Devoluções parciais" value={d.devolucoes_parciais} />}
                <DreLine label="Descontos" value={d.descontos} />
                <DreSpacer />

                {/* ── RECEITA LÍQUIDA ── */}
                <DreSubtotal label="= RECEITA LÍQUIDA" value={d.receita_liquida} color="cyan.700" />
                <DreLine label="CMV (Custo da Mercadoria)" value={d.cmv} />
                <DreSpacer />

                {/* ── LUCRO BRUTO ── */}
                <Flex bg="gray.100" px={4} py={3} justify="space-between" align="center" borderRadius="md"
                  borderLeft="4px solid" borderLeftColor="gray.700">
                  <Text fontWeight="bold" fontSize="md" color="gray.800">= LUCRO BRUTO</Text>
                  <Text fontWeight="bold" fontSize="md" color="gray.800">{formatMoney(d.lucro_bruto)}</Text>
                </Flex>
                <DreSpacer />

                {/* ── CUSTOS VARIÁVEIS ── */}
                <DreCollapsibleCost label="VENDAS" total={vendasTotalGeral} items={vendasItems} />
                <DreCollapsibleCost label="FRETE" total={freteTotalGeral} items={freteItems} />
                <DreCollapsibleCost label="CUSTO PROCESSAMENTO" total={processamentoTotal} items={processamentoItems} />
                {outrasCustoItems.map(item => (
                  <DreCollapsibleCost key={item.nome} label={item.nome.toUpperCase()} total={item.total} items={item.items} />
                ))}
                <DreSpacer />

                {/* ── MARGEM DE CONTRIBUIÇÃO ── */}
                <DreSubtotal label="= MARGEM DE CONTRIBUIÇÃO" value={margemContribuicao} color="purple.700" />
                <DreSpacer />

                {/* ── DESPESAS OPERACIONAIS ── */}
                {despesasCatEntries.length > 0 && (
                  <Box bg="purple.50" borderRadius="xl" overflow="hidden" border="1px solid" borderColor="purple.100">
                    <Flex p={4} justify="space-between" align="center">
                      <Text fontWeight="bold" color="purple.900">Despesas operacionais</Text>
                      <Text fontWeight="bold" color="purple.900">{formatMoney(despesasTotalLiq)}</Text>
                    </Flex>
                    <Box px={2} pb={2}>
                      {despesasCatEntries.map(([catNome, catData]) => (
                        <DreDespesaCatItem key={catNome} nome={catNome} catData={catData} />
                      ))}
                    </Box>
                  </Box>
                )}
                <DreSpacer />

                {/* ── LUCRO OPERACIONAL ── */}
                <DreSubtotal label="= LUCRO OPERACIONAL" value={lucroOperacional} color="green.700" />
                <DreSpacer />

                {/* ── RESULTADO FINANCEIRO ── */}
                <DreFinanceiroCollapsible financeiroGrupo={financeiroGrupo} />

                {/* ── IMPOSTO SOBRE VENDA ── */}
                <Flex px={4} py={2} justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="semibold" color="red.500">(-) Imposto sobre venda</Text>
                  <Text fontSize="sm" fontWeight="semibold" color="red.500">{formatMoney(d.impostos)}</Text>
                </Flex>
                <DreSpacer />

                {/* ── LUCRO LÍQUIDO ── */}
                <Box mt={4} p={5} bg={d.lucro_liquido >= 0 ? "green.500" : "red.500"} borderRadius="xl" color="white"
                  shadow="lg" sx={{ boxShadow: d.lucro_liquido >= 0 ? '0 10px 25px -5px rgba(16, 185, 129, 0.3)' : '0 10px 25px -5px rgba(239, 68, 68, 0.3)' }}>
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" textTransform="uppercase" letterSpacing="wider" opacity={0.8}>
                        Resultado Final
                      </Text>
                      <Text fontSize="xl" fontWeight="bold">= LUCRO LÍQUIDO</Text>
                    </Box>
                    <Text fontSize="2xl" fontWeight="black">{formatMoney(d.lucro_liquido)}</Text>
                  </Flex>
                  <Flex mt={3} pt={3} borderTop="1px solid" borderColor="whiteAlpha.400" justify="space-between" align="center">
                    <Text fontSize="sm" opacity={0.8}>Margem Líquida</Text>
                    <Badge bg="whiteAlpha.300" color="white" px={3} py={1} borderRadius="full" fontSize="sm" fontWeight="bold">
                      {formatPercent(d.margem_liquida)}
                    </Badge>
                  </Flex>
                </Box>

                <DreSpacer />

                {/* ── VARIAÇÃO DE ESTOQUE ── */}
                <DreVariacaoEstoque
                  variacao={d.variacao_estoque || 0}
                  detalhado={d.variacao_estoque_detalhado}
                />

                <DreSpacer />

                {/* ── GERAÇÃO DE CAIXA ── */}
                <Box p={4} bg={d.geracao_caixa >= 0 ? "green.50" : "orange.50"}
                  borderRadius="xl" border="2px dashed"
                  borderColor={d.geracao_caixa >= 0 ? "green.400" : "orange.400"}>
                  <Flex justify="space-between" align="center">
                    <HStack>
                      <Icon as={Banknote} boxSize={5} color={d.geracao_caixa >= 0 ? "green.600" : "orange.600"} />
                      <Box>
                        <Text fontSize="2xs" fontWeight="medium" textTransform="uppercase" letterSpacing="wider"
                          color={d.geracao_caixa >= 0 ? "green.700" : "orange.700"} opacity={0.8}>
                          Lucro Líquido + Variação de Estoque
                        </Text>
                        <Text fontSize="md" fontWeight="bold" color={d.geracao_caixa >= 0 ? "green.800" : "orange.800"}>
                          = GERAÇÃO DE CAIXA
                        </Text>
                      </Box>
                    </HStack>
                    <Text fontSize="xl" fontWeight="black" color={d.geracao_caixa >= 0 ? "green.800" : "orange.800"}>
                      {formatMoney(d.geracao_caixa)}
                    </Text>
                  </Flex>
                </Box>

              </VStack>
            ) : (
              <Text color="gray.400" textAlign="center" py={8}>Nenhum dado encontrado para o período</Text>
            )}
          </Card>

          {/* ==================== GRÁFICOS ==================== */}
          <VStack spacing={6}>
            <Card className="ring-0 w-full">
              <Title className="mb-4">Composição do Resultado</Title>
              {loading ? <Skeleton h="300px" w="100%" /> : waterfallData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={waterfallData} margin={{ top: 10, right: 10, left: 10, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} angle={-35} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                      {waterfallData.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <Text color="gray.400" textAlign="center" py={8}>Sem dados</Text>}
            </Card>

            <Card className="ring-0 w-full">
              <Title className="mb-4">Composição de Custos e Deduções</Title>
              {loading ? <Skeleton h="280px" w="100%" /> : custosPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={custosPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={95}
                      paddingAngle={3} dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                    >
                      {custosPieData.map((_, index) => <Cell key={index} fill={COLORS_PIE[index % COLORS_PIE.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Text color="gray.400" textAlign="center" py={8}>Sem dados</Text>}
            </Card>

            <Card className="ring-0 w-full">
              <Title className="mb-4">Status de Sincronização</Title>
              {loading ? (
                <VStack spacing={3}>{[...Array(4)].map((_, i) => <Skeleton key={i} h="20px" w="100%" />)}</VStack>
              ) : syncStatus ? (
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="sm" color="gray.600">Vendas com dados DRE</Text>
                      <Text fontSize="sm" fontWeight="bold" color="blue.600">{syncPercent}%</Text>
                    </Flex>
                    <Progress value={parseFloat(syncPercent)} colorScheme="blue" borderRadius="full" size="sm" />
                  </Box>
                  <SimpleGrid columns={2} spacing={3}>
                    <Box bg="gray.50" p={3} borderRadius="lg">
                      <Text fontSize="xs" color="gray.500">Total Vendas</Text>
                      <Text fontSize="lg" fontWeight="bold" color="gray.700">{syncStatus.total_vendas?.toLocaleString('pt-BR')}</Text>
                    </Box>
                    <Box bg="blue.50" p={3} borderRadius="lg">
                      <Text fontSize="xs" color="gray.500">Com Dados DRE</Text>
                      <Text fontSize="lg" fontWeight="bold" color="blue.600">{syncStatus.com_dados_dre?.toLocaleString('pt-BR')}</Text>
                    </Box>
                    <Box bg="orange.50" p={3} borderRadius="lg">
                      <Text fontSize="xs" color="gray.500">Sem Dados DRE</Text>
                      <Text fontSize="lg" fontWeight="bold" color="orange.600">{syncStatus.sem_dados_dre?.toLocaleString('pt-BR')}</Text>
                    </Box>
                    <Box bg="green.50" p={3} borderRadius="lg">
                      <Text fontSize="xs" color="gray.500">Sincronizados ML</Text>
                      <Text fontSize="lg" fontWeight="bold" color="green.600">{syncStatus.sincronizados_ml?.toLocaleString('pt-BR')}</Text>
                    </Box>
                  </SimpleGrid>
                  {syncStatus.pendentes_sync_ml > 0 && (
                    <Flex align="center" gap={2} bg="yellow.50" p={3} borderRadius="lg">
                      <Icon as={RefreshCw} boxSize={4} color="yellow.600" />
                      <Text fontSize="sm" color="yellow.700">
                        {syncStatus.pendentes_sync_ml} vendas ML pendentes de sincronização real
                      </Text>
                    </Flex>
                  )}
                  {d?.meta && (
                    <Box bg="purple.50" p={3} borderRadius="lg">
                      <Text fontSize="xs" color="gray.500" mb={1}>Custos Fixos Mensais Ativos</Text>
                      <Text fontSize="lg" fontWeight="bold" color="purple.600">{formatMoney(d.meta.custos_fixos_mensal)}</Text>
                    </Box>
                  )}
                </VStack>
              ) : <Text color="gray.400" textAlign="center" py={8}>Dados de sync não disponíveis</Text>}
            </Card>
          </VStack>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default DREConsolidado;
